/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  Accordion,
  AccordionHeader,
  AccordionPanel,
  Button,
  Typography2,
  breakpoints,
  useMediaQuery,
} from '@coursera/cds-core';
import type { AccordionProps } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import DurationMessage from 'bundles/enterprise-learning-path-description-page/components/syllabus/DurationMessage';
import type { TimeDuration } from 'bundles/enterprise-learning-path-description-page/components/syllabus/DurationMessage';
import type { CourseItemContentElement } from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemContent';
import type { CourseItemSubtitleElement } from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemSubtitle';
import type { SyllabusItemTitleElement } from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitle';
import SyllabusItemTitle from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitle';
import Modal, { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import xdpStyles from 'bundles/unified-description-page-common/constants/styles';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

const styles = {
  accordion: (hasDivider: boolean) => css`
    position: relative;
    padding: 0;
    margin: 0;
    ${hasDivider ? `margin-top: -8px;` : ''}

    ${hasDivider ? `border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);` : 'border-top: none;'}

    &::before {
      content: '';
      position: absolute;
      top: 0;
      height: 2px;
      left: var(--cds-spacing-200);
      right: var(--cds-spacing-200);
    }
  `,
  panel: css`
    margin: 0;
    padding: 0 var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200);
  `,
  button: css`
    justify-content: space-between;
    text-align: left;
    border-radius: 0;

    /* hide the focus ring that overlaps adjacent buttons */
    overflow: hidden;
  `,
  mobileSyllabusItem: css`
    & + & {
      ${xdpStyles.border('border-top')}
    }

    &:hover h3 {
      text-decoration: underline;
    }
  `,
  headerContent: css`
    display: flex;
    gap: var(--cds-spacing-200);
    min-width: 0;
  `,
  titleContent: css`
    min-width: 0;
  `,

  subTitleContainer: css`
    display: flex;
    margin-top: var(--cds-spacing-50);
    white-space: nowrap;

    ${breakpoints.down('sm')} {
      flex-wrap: wrap;
      white-space: unset;
    }
  `,
};

interface Props {
  children: [SyllabusItemTitleElement, CourseItemSubtitleElement, CourseItemContentElement];
  itemIndex: number;
  thumbnail?: React.ReactNode;
  timeDuration: TimeDuration | null;
}

function SyllabusItemRedesignV2({
  children,
  itemIndex,
  thumbnail,
  timeDuration,
}: Props): React.ReactElement<AccordionProps | 'div'> {
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const { isModalOpen, setModalOpen } = useModal();
  const [isExpanded, setIsExpanded] = React.useState(itemIndex === 1);
  const track = useTracker();
  const eventingProductData = useEventingProductData();
  const [title, , panel] = React.Children.toArray(children) as [
    SyllabusItemTitleElement,
    CourseItemSubtitleElement,
    CourseItemContentElement,
  ];

  const handleAccordionChange = React.useCallback(
    (event: React.ChangeEvent<unknown>, expanded: boolean) => {
      if (expanded) {
        track('click_button', {
          button: {
            name: 'skill_pathway_skill_area_expand_accordion',
          },
        });
      }
      setIsExpanded(expanded);
    },
    [track]
  );

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'course_modules_card',
      },
      ...eventingProductData,
    }
  );

  if (isMobile) {
    return (
      <div css={styles.mobileSyllabusItem} className="syllabus-item-redesign-v2" ref={visibilityTrackingRef}>
        <Button
          variant="ghost"
          icon={<ChevronNextIcon size="medium" />}
          css={styles.button}
          fullWidth
          onClick={setModalOpen}
        >
          <div css={styles.headerContent}>
            <div>
              <SyllabusItemTitle {...title.props} disableLink={true} />
              {timeDuration && (
                <Typography2 variant="bodySecondary" color="supportText" component="div" css={styles.subTitleContainer}>
                  <DurationMessage timeDuration={timeDuration} />
                </Typography2>
              )}
            </div>
          </div>
        </Button>
        <Modal open={isModalOpen} setOpen={setModalOpen} header={title} hideActions>
          {panel}
        </Modal>
      </div>
    );
  }

  return (
    <Accordion
      variant="silent"
      css={styles.accordion(itemIndex > 1)}
      className="syllabus-item-redesign-v2"
      expanded={isExpanded}
      onChange={handleAccordionChange}
      ref={visibilityTrackingRef}
    >
      <AccordionHeader
        label={title.props.title}
        indicatorAlignment="end"
        supportText={timeDuration ? <DurationMessage timeDuration={timeDuration} /> : undefined}
      />
      <AccordionPanel css={styles.panel}>{panel}</AccordionPanel>
    </Accordion>
  );
}

export default SyllabusItemRedesignV2;
